import { Accordion, Button } from '@zoom/components';
import { isAfter } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { FormState } from 'final-form';
import { equals } from 'ramda';
import React, { memo, useEffect, useState } from 'react';
import { FormSpy } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { ComposedInfobox } from '../../../components/ComposedInfobox/ComposedInfobox';
import { ModalConfirmation } from '../../../components/ModalConfirmation';
import { SidebarBody, SidebarFormWrapper, SidebarHeader } from '../../../components/layout/Sidebar';
import { getDateTime, getTimeValue } from '../../Scheduling/SchedulingComposer/ScheduleConfigurationSidebar';
import {
  ADJUSTMENTS_CONSTRAINS,
  Adjustment,
  AdjustmentChange,
  AdjustmentChangeType,
  adjustmentInChangeLabelKey,
} from '../config';
import { AdjustmentForm } from './AdjustmentForm';
import { AdjustmentTile } from './AdjustmentTile';

export interface ForecastAdjustmentsSidebarProps {
  addNewAdjustment: (adjustment: Adjustment) => void;
  adjustments: Adjustment[];
  adjustmentsMutationInProgress: boolean;
  containsError: boolean;
  containsRangeCollision: boolean;
  concurrentChatsPresetValue?: number | null;
  deleteAdjustment: (adjustment: Adjustment) => void;
  handleDateRangeChange: (startDateTime: Date, endDateTime: Date, id?: string) => void;
  handleFormReset: () => void;
  handlePreview: (id: string, opened: boolean) => void;
  maxDate: Date;
  minDate: Date;
  preOpenedAdjustmentId: string[];
  queueTimezone: string;
  updateAdjustment: (adjustment: Adjustment) => void;
}

type ModalContent = {
  type: AdjustmentChangeType;
  adjustment: Adjustment;
};

type FormValues = Pick<
  Adjustment,
  'averageHandlingTime' | 'contactVolume' | 'shrinkagePercentage' | 'name' | 'concurrentChatsCount'
> & {
  id?: string;
  isEdit?: boolean;
  startDateTime: Date;
  endDateTime: Date;
  startTime: string;
  endTime: string;
};

export const ForecastAdjustmentsSidebarComponent: React.FC<ForecastAdjustmentsSidebarProps> = ({
  addNewAdjustment,
  adjustments,
  adjustmentsMutationInProgress,
  containsError,
  containsRangeCollision,
  deleteAdjustment,
  handleDateRangeChange,
  handleFormReset,
  handlePreview,
  concurrentChatsPresetValue,
  maxDate,
  minDate,
  preOpenedAdjustmentId,
  queueTimezone,
  updateAdjustment,
}) => {
  const [modalContent, setModalContent] = useState<ModalContent | null>(null);

  const [shouldShowCollisions, setShouldShowCollisions] = useState(false);
  const [isSelectedValidRange, setIsSelectedValidRange] = useState(true);

  const formInitialValues: FormValues = {
    averageHandlingTime: ADJUSTMENTS_CONSTRAINS.averageHandlingTime.default,
    contactVolume: ADJUSTMENTS_CONSTRAINS.contactVolume.default,
    endDateTime: maxDate,
    endTime: getTimeValue(maxDate.getTime()),
    isEdit: false,
    name: '',
    shrinkagePercentage: ADJUSTMENTS_CONSTRAINS.shrinkage.default,
    startDateTime: minDate,
    startTime: getTimeValue(minDate.getTime()),
    concurrentChatsCount: concurrentChatsPresetValue,
  };
  const { t } = useTranslation();

  const onSubmit = ({
    averageHandlingTime,
    concurrentChatsCount,
    contactVolume,
    endDateTime,
    id,
    isEdit,
    name,
    shrinkagePercentage,
    startDateTime,
    endTime,
    startTime,
  }: FormValues) => {
    const adjustmentData: Adjustment = {
      averageHandlingTime,
      concurrentChatsCount,
      contactVolume,
      name,
      shrinkagePercentage,
      dateTimeRange: {
        startDateTime: zonedTimeToUtc(
          getDateTime(startTime, startDateTime.getTime(), true),
          queueTimezone
        ).toISOString(),
        endDateTime: zonedTimeToUtc(getDateTime(endTime, endDateTime.getTime(), true), queueTimezone).toISOString(),
      },
      id: id || v4(),
    };

    if (isEdit) {
      if (containsRangeCollision) {
        setModalContent({ adjustment: adjustmentData, type: AdjustmentChange.EDIT });
      } else {
        updateAdjustment(adjustmentData);
      }
    } else if (containsRangeCollision) {
      setModalContent({ adjustment: adjustmentData, type: AdjustmentChange.CREATE });
    } else {
      addNewAdjustment(adjustmentData);
    }
  };

  const handleToggleItem = (adjustmentId: string, isOpen: boolean) => {
    handlePreview(adjustmentId, isOpen);
  };

  return (
    <SidebarFormWrapper<FormValues>
      className="forecasting-studio__sidebar"
      onSubmit={onSubmit}
      initialValues={formInitialValues}
      initialValuesEqual={equals}
      name="forecast-adjustments-form"
    >
      {({ active, dirty, form: { blur, reset }, valid, values }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (!adjustmentsMutationInProgress && valid && !containsError) {
            if (active) {
              blur(active);
            }

            setShouldShowCollisions(false);
            reset(formInitialValues);
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [adjustmentsMutationInProgress]);

        const handleEdit = (adjustment: Adjustment) => {
          const initialValues = {
            ...adjustment,
            dateTimeRange: undefined,
            startDateTime: utcToZonedTime(adjustment.dateTimeRange.startDateTime, queueTimezone),
            startTime: getTimeValue(utcToZonedTime(adjustment.dateTimeRange.startDateTime, queueTimezone).getTime()),
            endDateTime: utcToZonedTime(adjustment.dateTimeRange.endDateTime, queueTimezone),
            endTime: getTimeValue(utcToZonedTime(adjustment.dateTimeRange.endDateTime, queueTimezone).getTime()),
            isEdit: true,
          };

          reset(initialValues);
          handleDateRangeChange(initialValues.startDateTime, initialValues.endDateTime, adjustment.id);
        };

        const handleFormValuesChange = ({
          active: isActive,
          values: { id, startDateTime, endDateTime, startTime, endTime },
          dirty: isDirty,
        }: FormState<FormValues>) => {
          const startWithTime =
            startDateTime && startTime ? (getDateTime(startTime, startDateTime.getTime(), true) as Date) : null;
          const endWithTime =
            endDateTime && endTime ? (getDateTime(endTime, endDateTime.getTime(), true) as Date) : null;

          const validRange =
            !!startWithTime && !!endWithTime && isAfter(endWithTime, startWithTime) && !isAfter(endWithTime, maxDate);

          if (
            isDirty &&
            validRange &&
            (values.startDateTime !== startDateTime ||
              values.endDateTime !== endDateTime ||
              values.startTime !== startTime ||
              values.endTime !== endTime)
          ) {
            handleDateRangeChange(startWithTime, endWithTime, id);
          } else if ((isActive || isDirty) && !shouldShowCollisions && validRange) {
            setShouldShowCollisions(true);
            handleDateRangeChange(startWithTime, endWithTime, id);
          }

          setIsSelectedValidRange(validRange);
        };

        const handleResetFormValues = () => {
          if (values.isEdit) {
            reset(formInitialValues);
          } else {
            reset();
          }

          setShouldShowCollisions(false);
          handleFormReset();

          if (active) {
            blur(active);
          }
        };

        const renderAccordionLabel = ({ name }: Adjustment) => name;

        const handleModalSubmit = () => {
          if (!modalContent) {
            return;
          }

          if (modalContent.type === AdjustmentChange.CREATE) {
            addNewAdjustment(modalContent.adjustment);
          }

          if (modalContent.type === AdjustmentChange.EDIT) {
            updateAdjustment(modalContent.adjustment);
          }

          if (modalContent.type === AdjustmentChange.DELETE) {
            deleteAdjustment(modalContent.adjustment);
          }

          setModalContent(null);
        };

        return (
          <>
            <FormSpy subscription={{ values: true, active: true, dirty: true }} onChange={handleFormValuesChange} />
            <SidebarHeader>
              <Button
                className="mr-auto"
                data-test-id="adjustment-form:apply-button"
                disabled={!valid || !dirty || !isSelectedValidRange}
                kind="secondary"
                type="submit"
              >
                {t('form.apply')}
              </Button>
              <Button
                data-test-id="adjustment-form:reset-button"
                disabled={!values.isEdit && !dirty}
                kind="link"
                onClick={handleResetFormValues}
              >
                {t('form.resetChanges')}
              </Button>
            </SidebarHeader>
            <SidebarBody>
              <AdjustmentForm
                values={values}
                minDate={minDate}
                maxDate={maxDate}
                isChatQueueType={!!concurrentChatsPresetValue}
              >
                <ComposedInfobox
                  additionalMessage={containsRangeCollision && t('forecast.adjustmentsWarning')}
                  dataTestId="adjustment-infobox"
                >
                  {t('forecast.adjustmentsInfoMessage')}
                </ComposedInfobox>
              </AdjustmentForm>
              {!!adjustments.length && (
                <div className="applied-adjustments-container">
                  <p>{t('forecast.appliedAdjustments')}</p>
                  <Accordion<Adjustment>
                    allowMultipleOpened
                    items={adjustments}
                    dataTestId="applied-adjustments"
                    label={renderAccordionLabel}
                    toggleCallback={handleToggleItem}
                    preOpenedItemIds={preOpenedAdjustmentId.length ? preOpenedAdjustmentId : undefined}
                  >
                    {(item, { isOpen }) => (
                      <AdjustmentTile
                        adjustment={item}
                        handleEdit={handleEdit}
                        handleDelete={(adjustment) => setModalContent({ adjustment, type: AdjustmentChange.DELETE })}
                        isOpen={isOpen}
                        isPreOpen={preOpenedAdjustmentId?.[0] === item.id}
                        timezone={queueTimezone}
                      />
                    )}
                  </Accordion>
                </div>
              )}
            </SidebarBody>
            <ModalConfirmation
              headerText={modalContent ? t(`forecast.adjustment${adjustmentInChangeLabelKey[modalContent.type]}`) : ''}
              bodyText={
                modalContent ? t(`forecast.adjustment${adjustmentInChangeLabelKey[modalContent.type]}Body`) : ''
              }
              submitText={t('layout.confirm.yes')}
              closeText={t('layout.confirm.no')}
              onSubmit={handleModalSubmit}
              onClose={() => setModalContent(null)}
              show={!!modalContent}
            />
          </>
        );
      }}
    </SidebarFormWrapper>
  );
};

export const ForecastAdjustmentsSidebar = memo(ForecastAdjustmentsSidebarComponent);
